import { useState } from "react";

import type { Yorum } from "@/api/yorumlar";
import { DeepLink } from "@/components/DeepLink";
import { Pill } from "@/components/ui";
import { t } from "@/i18n";
import { fmtDate } from "@/lib/utils";

/** Govde bu uzunlugu asarsa 3 satira kirpilir + "Devamını göster" gosterilir. */
const GOVDE_KIRPMA_ESIGI = 200;

function Yildizlar({ rating }: { rating: number | null }) {
  const r = Math.max(0, Math.min(5, Math.round(rating || 0)));
  if (!r) {
    return <span className="text-xs text-muted">{t("yorumlar.puansiz")}</span>;
  }
  return (
    <span aria-label={`${r}/5`} className="text-amber">
      {"★".repeat(r)}
      <span className="text-line">{"★".repeat(5 - r)}</span>
    </span>
  );
}

/**
 * Tek yorum karti — kompakt: baslik satiri (yildiz + rozetler), govde en
 * fazla 3 satir (uzunsa "Devamını göster" ile acilir), alt satir meta
 * (musteri, urun, tarih). eski frontend/legacy/js/yorumlar.js `yorumKarti()`
 * React karsiligi, 17.09.2026 tasarim rehberiyle sadelestirildi.
 */
export function YorumKart({ yorum }: { yorum: Yorum }) {
  const [acik, setAcik] = useState(false);
  const govde = yorum.body ?? "";
  const uzun = govde.length > GOVDE_KIRPMA_ESIGI;

  return (
    <div className="card space-y-2" data-testid="yorum-karti">
      <div className="flex flex-wrap items-center gap-2">
        <Yildizlar rating={yorum.rating} />
        {yorum.is_approved === false && (
          <Pill tone="warn">
            <span data-testid="yorum-onay-bekliyor">{t("yorumlar.rozet.onay_bekliyor")}</span>
          </Pill>
        )}
        {yorum.title && <span className="text-sm font-semibold text-ink">{yorum.title}</span>}
        {yorum.is_verified_purchase && <Pill tone="ok">{t("yorumlar.satin_aldi")}</Pill>}
        {yorum.recommended && <Pill tone="info">{t("yorumlar.rozet.oneriyor")}</Pill>}
        {(yorum.useful ?? 0) > 0 && <Pill tone="muted">{t("yorumlar.rozet.faydali", { n: String(yorum.useful) })}</Pill>}
      </div>

      {govde ? (
        <div>
          <p className={`text-sm text-ink ${acik ? "" : "line-clamp-3"}`}>{govde}</p>
          {uzun && (
            <button
              type="button"
              onClick={() => setAcik((v) => !v)}
              aria-expanded={acik}
              className="mt-1 text-xs font-semibold text-brand-primary hover:underline"
            >
              {acik ? t("yorumlar.govde.daha_az") : t("yorumlar.govde.devamini_goster")}
            </button>
          )}
        </div>
      ) : (
        <p className="text-sm">
          <em className="text-muted">{t("yorumlar.metin_yok")}</em>
        </p>
      )}

      {yorum.answer && (
        <div className="rounded-md border border-line bg-bg p-2 text-sm text-ink">
          <strong className="font-semibold">{t("yorumlar.magaza_cevabi")}:</strong> {yorum.answer}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 text-xs text-muted">
        <span>{yorum.customer_name || t("yorumlar.anonim")}</span>
        <DeepLink href={yorum.admin_link} label={yorum.product_name || "—"} />
        <span>{fmtDate(yorum.created_at_platform)}</span>
      </div>
    </div>
  );
}
